import React, { useCallback, useEffect, useRef, useState } from "react";
import { CandidateCard } from "@component/candidate-card";
import { FilterModal } from "@component/filter-modal";
import { Button } from "@component/button";
import { BookmarkIcon, ChartNoAxesColumn, FilterIcon, Loader2Icon, PlusIcon, SearchIcon, UsersIcon, XIcon } from "lucide-react";
import { searchCandidates, SearchCandidatesRequest } from "@service/employer";
import { Candidate, SearchCandidateFilter } from "@type/candidate";
import { MetaPagination } from "@type/common";
import { defaultPagination } from "@constant/app";
import { EmptyState } from "@component/empty-state";
import { cn } from "@util/tailwind";
import { useUserProfileContext } from "@context/user_profile";
import { getCookie, setCookie } from "@util/cookie";
import { getBookmaredCandidates, getShortlistedCandidates } from "@service/user";
import LoadingBox from "@component/loading-box";
import DrawerCandidateProfile from "./drawer/drawer-candidate-profile";
import CompanyMetrics from "./company-metrics";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";

const DEFAULT_PAGINATION = {
    page: defaultPagination.page,
    limit: defaultPagination.limit,
    total: 0,
    total_pages: 0
}

export function CandidateDirectory() {
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [filter, setFilter] = useState<SearchCandidateFilter>({
        skills: "",
        field_of_study: "",
        company: "",
        work_type: "",
        location: "",
        degree: "",
        country: "",
        university: "",
        title: "",
        min_experience: 0,
        max_experience: 40,
        candidate_type: []
    });
    const [countFilter, setCountFilter] = useState<number>(0);
    const [isHasFilter, setHasFilter] = useState<boolean>(false);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [paginationMetadata, setPaginationMetadata] = useState<MetaPagination>(DEFAULT_PAGINATION)
    const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
    const [showRecentSearches, setShowRecentSearches] = useState<boolean>(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [candidateSaved, setCandidateSaved] = useState<string[]>([]);
    const [showSavedOnly, setShowSavedOnly] = useState<boolean>(false);
    const [reloadSavedBookmark, setReloadSavedBookmark] = useState<number>(0);
    const params = new URLSearchParams(window.location.search);
    const [activeTab, setActiveTab] = useState(() => {
        const tab = new URLSearchParams(window.location.search).get("tab");
        return tab || "analytics";
    });
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const tab = params.get("tab");
        if (tab) {
            setActiveTab(tab);
        }
    }, [params.get("tab")]);

    const handleChangeTab = (tab: string) => {
        setActiveTab(tab);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", tab);
        window.history.pushState({}, "", url.toString());
    };

    const fetchCandidates = useCallback(async (currentPage: number, keyword: string, isLoadMore: boolean) => {
        if (isLoadMore) {
            setIsLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const searchData: SearchCandidatesRequest = {
                page: currentPage,
                limit: defaultPagination.limit,
                job_title: keyword,
                job_description: keyword,
                filters: filter,
            };

            if (showSavedOnly) {
                const savedCandidates: string[] = getCookie(`saved_candidate:${currentUserInfo?.id}`);
                const updatedFilters: SearchCandidateFilter = {
                    ...filter,
                    saved_candidates: savedCandidates || [],
                };
                searchData.filters = updatedFilters;
            }

            const response = await searchCandidates(searchData);

            if (response) {
                if (isLoadMore) {
                    setCandidates((prevJobs: Candidate[]) => {
                        return [...prevJobs, ...response?.data?.data.candidates || []];
                    });
                } else {
                    setCandidates(response.data?.data.candidates as Candidate[]);
                }
                setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION)
            } else {
                setError("Failed to fetch candidates");
                setCandidates([]);
                setPaginationMetadata(DEFAULT_PAGINATION)
            }
        } catch (err) {
            setError(`An unexpected error occurred`);
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [filter, showSavedOnly]);

    // Fetch candidates from API
    useEffect(() => {
        fetchCandidates(defaultPagination.page, "", false);

        setRecentSearches(() => {
            const storedSearches = localStorage.getItem("recentSearchesCandidates");
            return storedSearches ? JSON.parse(storedSearches) : [];
        });
    }, []);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target as Node)
            ) {
                setShowRecentSearches(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isLoadingUserInfo && currentUserInfo) {
            getBookmaredCandidates().then((resp) => {
                if (resp.status === 200) {
                    setCandidateSaved(resp.data.data);
                    setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                }
            })

            getShortlistedCandidates().then((resp) => {
                if (resp.status === 200) {
                    setCookie(`shortlisted_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                }
            })
        }
    }, [isLoadingUserInfo, currentUserInfo])

    const handleFilterModalClose = () => {
        setIsFilterModalOpen(false);
    };

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (searchQuery === "") return

        setShowRecentSearches(false);
        setIsSearching(true);
        setError(null);

        try {
            const searchData: SearchCandidatesRequest = {
                page: defaultPagination.page,
                limit: defaultPagination.limit,
                job_title: searchQuery,
                job_description: searchQuery,
                filters: filter
            };

            const response = await searchCandidates(searchData);
            if (response) {
                setCandidates(response.data?.data.candidates as Candidate[]);
                setHasSearched(true);
                setCurrentPage(defaultPagination.page);
            }

            localStorage.setItem("recentSearchesCandidates", JSON.stringify([searchQuery, ...recentSearches]));
            setRecentSearches((prev) => {
                const updatedSearches = [searchQuery, ...prev];
                if (updatedSearches.length > 5) {
                    updatedSearches.shift(); // Remove oldest search if more than 5
                }
                return updatedSearches;
            });

            setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION);
        } catch (err) {
            setError("An unexpected error occurred");
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            setIsSearching(false);
        }
    };

    const handleSaveRecentSearch = useCallback(async (search: string, filter: SearchCandidateFilter) => {
        setSearchQuery(search);
        setShowRecentSearches(false);
        setIsSearching(true);
        setError(null);

        try {
            const searchData: SearchCandidatesRequest = {
                page: defaultPagination.page,
                limit: defaultPagination.limit,
                job_title: search,
                job_description: search,
                filters: filter
            };

            const response = await searchCandidates(searchData);
            if (response) {
                setCandidates(response.data?.data.candidates as Candidate[]);
                setHasSearched(true);
                setCurrentPage(defaultPagination.page);
            }

            setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION);
        } catch (err) {
            setError("An unexpected error occurred");
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            setIsSearching(false);
        }
    }, [showSavedOnly])

    const onChangeSearch = (value: string) => {
        setSearchQuery(value)
        if (hasSearched && value === "") {
            fetchCandidates(defaultPagination.page, "", false);
            setHasSearched(false);
        }
    }

    const handleLoadMore = async (nextPage: number) => {
        setCurrentPage(nextPage);
        await fetchCandidates(nextPage, searchQuery, true);
    };

    const handleReset = async () => {
        setFilter({
            skills: "",
            field_of_study: "",
            company: "",
            work_type: "",
            location: "",
            degree: "",
            country: "",
            university: "",
            title: "",
            min_experience: 0,
            max_experience: 40,
            candidate_type: []
        });

        setCountFilter(0);
        setHasFilter(false);

        handleSaveRecentSearch(searchQuery, {
            skills: "",
            field_of_study: "",
            company: "",
            work_type: "",
            location: "",
            degree: "",
            country: "",
            university: "",
            title: "",
            min_experience: 0,
            max_experience: 40,
            candidate_type: [],
            saved_candidates: showSavedOnly ? candidateSaved : []
        });
    };

    const handleFilter = useCallback(async (filter: SearchCandidateFilter) => {
        setIsSearching(true);
        setLoading(true);
        setError(null);

        try {
            const searchData: SearchCandidatesRequest = {
                page: defaultPagination.page,
                limit: defaultPagination.limit,
                job_title: searchQuery,
                job_description: searchQuery,
                filters: filter
            };

            if (showSavedOnly) {
                const savedCandidates: string[] = getCookie(`saved_candidate:${currentUserInfo?.id}`);
                searchData.filters = {
                    ...filter,
                    saved_candidates: savedCandidates || [],
                }
            }

            const response = await searchCandidates(searchData);
            if (response) {
                setCandidates(response.data?.data.candidates as Candidate[]);
                setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION);

                setCurrentPage(defaultPagination.page);
            }

            let activeFiltersCount = 0;
            if (filter.skills) activeFiltersCount++;
            if (filter.field_of_study) activeFiltersCount++;
            if (filter.company) activeFiltersCount++;
            if (filter.work_type) activeFiltersCount++;
            if (filter.location) activeFiltersCount++;
            if (filter.degree) activeFiltersCount++;
            if (filter.country) activeFiltersCount++;
            if (filter.university) activeFiltersCount++;
            if (filter.title) activeFiltersCount++;
            if (filter.candidate_type.length > 0) activeFiltersCount += filter.candidate_type.length;
            if (filter.shortlisted) activeFiltersCount++;
            if (filter.completed_interview) activeFiltersCount++;
            if (filter.ai_interview_status && filter.ai_interview_status.length > 0) activeFiltersCount += filter.ai_interview_status.length;
            if (filter.min_experience > 0 || filter.max_experience < 40)
                activeFiltersCount++;

            if (activeFiltersCount > 0) {
                setHasSearched(true);
            }

            setCountFilter(activeFiltersCount);
            setHasFilter(activeFiltersCount > 0);
        } catch (err) {
            setError("An unexpected error occurred");
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            setIsSearching(false);
            setLoading(false);
        }
    }, [showSavedOnly, searchQuery]);

    const handleOnShowSavedCandidate = useCallback(() => {
        setShowSavedOnly(!showSavedOnly)
        const savedCandidates: string[] = getCookie(`saved_candidate:${currentUserInfo?.id}`);
        setCandidateSaved(savedCandidates || [])
        console.log('showSavedOnly', !showSavedOnly);
        handleSaveRecentSearch(searchQuery, {
            ...filter,
            saved_candidates: !showSavedOnly ? (savedCandidates || []).map(item => item) : []
        });
    }, [showSavedOnly, filter, searchQuery])

    const handleReloadSavedCandidate = useCallback(() => {
        setReloadSavedBookmark(prev => prev + 1); // for trigger useEffect on drawer profile bookmark icon
        if (!showSavedOnly) return

        const savedCandidates: string[] = getCookie(`saved_candidate:${currentUserInfo?.id}`);
        setCandidateSaved(savedCandidates || [])
        handleFilter({
            ...filter,
            saved_candidates: savedCandidates,
        })
    }, [showSavedOnly, filter])

    return (
        <>
            <Tabs defaultValue="analytics" value={activeTab} onValueChange={handleChangeTab} className="w-full">
                <div className="pt-4 sm:pt-6 mt-8 flex justify-between items-center mb-4 ml-16">
                    <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                        <TabsTrigger
                            value="analytics"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <ChartNoAxesColumn className="h-4 w-4" />
                            Hiring Analytics
                        </TabsTrigger>
                        <TabsTrigger
                            value="talent-pool"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <UsersIcon className="h-4 w-4" />
                            Talent Pool
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="talent-pool" className="mt-0">
                    <div className="flex h-[calc(100vh-100px)] overflow-hidden pt-1">
                        <div className={cn("flex flex-col border-r border-gray-200 dark:border-gray-800 px-4 sm:px-16", {
                            "w-full": selectedCandidate === null,
                            "w-1/2": selectedCandidate !== null
                        })}>
                            <div className="flex flex-col space-y-2 sm:space-y-4 mb-6">
                                <form className="flex items-center space-x-2" onSubmit={handleSearchSubmit}>
                                    <div className="relative flex-1">
                                        <div className="relative flex-grow">
                                            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                placeholder={countFilter > 0 ? "Currently searching candidates with applied filters" : "Try searching 'Python developer with 4 years experience'"}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 dark:bg-gray-800 dark:text-white"
                                                value={searchQuery}
                                                onChange={(e) => onChangeSearch(e.target.value)}
                                                onFocus={() => {
                                                    if (recentSearches.length > 0) {
                                                        setShowRecentSearches(true);
                                                    }
                                                }}
                                            />

                                            {isSearching && (
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                    <Loader2Icon className="h-4 w-4 text-blue-600 animate-spin" />
                                                </div>
                                            )}

                                            {hasSearched && !isSearching && (
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                                                        displaying {paginationMetadata?.total || 0} results
                                                    </span>

                                                    {searchQuery && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onChangeSearch("")}
                                                            className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                                            aria-label="Clear search"
                                                        >
                                                            <XIcon className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {showRecentSearches && recentSearches.length > 0 && (
                                            <div
                                                ref={dropdownRef}
                                                className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg z-10"
                                            >
                                                <div className="p-2 flex flex-row justify-between text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                                                    Recent Searches

                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setRecentSearches([]);
                                                            localStorage.removeItem("recentSearchesCandidates");
                                                        }}
                                                        className="text-blue-500 hover:text-blue-700"
                                                    >
                                                        Clear all
                                                    </button>
                                                </div>
                                                <div>
                                                    {recentSearches.map((search, index) => (
                                                        <div key={index}>
                                                            <div
                                                                onClick={() => handleSaveRecentSearch(search, filter)}
                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                                                            >
                                                                <SearchIcon className="h-3 w-3 text-gray-400" />

                                                                {search}

                                                                {/* Add button clear */}
                                                                <div className="flex-1"></div>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const updatedSearches = recentSearches.filter((_, i) => i !== index);
                                                                        setRecentSearches(updatedSearches);
                                                                        localStorage.setItem("recentSearchesCandidates", JSON.stringify(updatedSearches));
                                                                    }}
                                                                    className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                                                    aria-label="Remove from recent searches"
                                                                >
                                                                    <XIcon className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        type="submit"
                                        variant="default"
                                        disabled={isSearching || searchQuery.length === 0}
                                        className="bg-blue-600 hover:bg-blue-800 h-[42px]"
                                    >
                                        Search
                                    </Button>
                                </form>
                                {searchQuery.length > 0 && isHasFilter && (
                                    <div className="mt-2 flex items-center gap-2 text-xs text-blue-700">
                                        <div className="flex items-center">
                                            <SearchIcon className="h-3 w-3 mr-1" />

                                            <span className="font-medium">"{searchQuery}"</span>
                                        </div>

                                        <PlusIcon className="h-3 w-3 mx-1" />
                                        <span className="font-medium">
                                            {countFilter} active{" "}
                                            {countFilter === 1 ? "filter" : "filters"}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-start items-center mt-3 gap-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-blue-600 flex !bg-white items-center gap-2 p-0 h-auto"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsFilterModalOpen(true);
                                        }}
                                    >
                                        <FilterIcon className="h-4 w-4" />
                                        Edit filters
                                    </Button>
                                    {countFilter > 0 && (
                                        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950 px-3 py-1 rounded-md"
                                            style={{ borderRadius: "0.75rem", backgroundColor: "#dbeafe" }}
                                        >
                                            <span className="text-sm text-blue-600 dark:text-blue-400">
                                                {countFilter}{" "}
                                                {countFilter === 1 ? "filter" : "filters"} applied
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReset();
                                                }}
                                                className="hover:bg-blue-200 rounded-full p-0.5"
                                                aria-label="Clear all filters"
                                            >
                                                <XIcon className="text-blue-600 h-3 w-3" />
                                            </button>
                                        </div>
                                    )}

                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        type="button"
                                        onClick={handleOnShowSavedCandidate}
                                        className={`flex items-center !bg-white gap-2 h-8 px-0 ${showSavedOnly
                                            ? "text-blue-600"
                                            : "text-blue-600"
                                            }`}
                                    >
                                        <BookmarkIcon className={cn("h-4 w-4 transition-colors", {
                                            "fill-blue-600": showSavedOnly,
                                        })} />
                                        <span>Saved Candidates</span>
                                    </Button>
                                    {showSavedOnly && candidateSaved.length > 0 && (
                                        <div
                                            className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950 px-3 py-1 rounded-md"
                                            style={{ borderRadius: "0.75rem", backgroundColor: "#dbeafe" }}
                                        >
                                            <span className="text-sm text-blue-600 dark:text-blue-400">
                                                Showing {candidateSaved.length} candidate
                                                {candidateSaved.length > 1 ? "s" : ""}
                                            </span>
                                            <button
                                                onClick={handleOnShowSavedCandidate}
                                                className="hover:bg-blue-200 rounded-full p-0.5"
                                            >
                                                <XIcon className="text-blue-600 h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                                <style>
                                    {`
                                .hide-scrollbar::-webkit-scrollbar {
                                    display: none;
                                }
                            `}
                                </style>
                                <div className="hide-scrollbar"></div>
                                {isLoading ? (
                                    <LoadingBox />
                                ) : showSavedOnly && candidateSaved.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4">
                                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                            <BookmarkIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                            No Saved Candidates
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
                                            You haven't saved any candidates yet.
                                            <br />
                                            Click the bookmark icon to save candidates.
                                        </p>
                                    </div>
                                ) : (candidates || []).length > 0 ? (
                                    <div className="pb-6">
                                        <div className={cn("grid gap-4", {
                                            "grid-cols-1 md:grid-cols-2": selectedCandidate === null,
                                            "grid-cols-1": selectedCandidate !== null
                                        })}>
                                            {candidates.map((candidate, index) => (
                                                <CandidateCard
                                                    key={index}
                                                    isSelected={selectedCandidate?.cv_id === candidate.cv_id}
                                                    candidate={candidate}
                                                    hasSearched={hasSearched}
                                                    isLoadNextPage={currentPage > 1}
                                                    searchQuery={searchQuery}
                                                    onSelectedCandidate={() => setSelectedCandidate(candidate)}
                                                    onReloadSavedCandidate={handleReloadSavedCandidate}
                                                    reloadSavedBookmark={reloadSavedBookmark}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <EmptyState
                                        icon={
                                            (
                                                <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                                            )
                                        }
                                        title="No candidates found"
                                        description="We couldn't find any candidates matching your search criteria. Try adjusting your filters or search terms."
                                        action={
                                            (
                                                <Button
                                                    variant="default"
                                                    className="flex items-center gap-2"
                                                    onClick={() => setIsFilterModalOpen(true)
                                                    }
                                                >
                                                    <FilterIcon className="h-4 w-4" />
                                                    Edit filters
                                                </Button>
                                            )}
                                    />
                                )}

                                {(candidates || []).length > 0 &&
                                    currentPage != paginationMetadata?.total_pages && (
                                        <div className="mb-6 flex justify-center">
                                            <Button
                                                variant="outline"
                                                className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                                onClick={() => handleLoadMore(currentPage + 1)}
                                                disabled={isLoadingMore}
                                            >
                                                {isLoadingMore ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                                                        Loading...
                                                    </div>
                                                ) : (
                                                    "Load More"
                                                )}
                                            </Button>
                                        </div>
                                    )}
                            </div>
                        </div>
                        {selectedCandidate && (
                            <div className="w-1/2">
                                <DrawerCandidateProfile
                                    isOpen={!!selectedCandidate}
                                    cvID={selectedCandidate.cv_id}
                                    candidate={selectedCandidate.cv_data}
                                    user={selectedCandidate.user}
                                    reloadSavedBookmark={reloadSavedBookmark}
                                    onClose={() => setSelectedCandidate(null)}
                                    onReloadSavedBookmark={() => {
                                        setReloadSavedBookmark(prev => prev + 1);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="analytics" className="mt-0">
                    <div className={cn("flex flex-col border-r border-gray-200 dark:border-gray-800 px-4 sm:px-16 w-full")}>
                        <CompanyMetrics />
                    </div>
                </TabsContent>
            </Tabs>
            {
                isFilterModalOpen && (
                    <FilterModal
                        filter={filter}
                        onClose={handleFilterModalClose}
                        onReset={handleReset}
                        onChange={(key, value) => {
                            setFilter((prev) => ({
                                ...prev,
                                [key]: value,
                            }));
                        }}
                        onFilter={handleFilter}
                    />
                )
            }
        </>
    );
}